import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { Form, Row, Col } from "react-bootstrap";

import { hexToRGBA } from "../../../../../utils/theme";
import { SUFFIX_OPTIONS } from "../../../../../constants/forms";
import { state_array as STATES } from "../../../../../../variables/Variables";
import { usePrevious } from "../../../../../hooks";

import TextInput from "../../../controls/TextInput";
import MaskInput from "../../../controls/MaskInput";
import Select from "../../../controls/Select";
import Button from "../../../styled/Button";

const StyledOrderStatusResultForm = styled(Form)`
  padding-bottom: 62px;

  .inputs-block-title {
    margin-bottom: 32px;
    padding-left: 24px;
    padding-right: 24px;

    font-family: 'RobotoBold';
    font-size: 24px;
    line-height: calc(1.42857rem * (24 / 16));
    text-align: center;
    text-transform: capitalize;
    color: #002346;

    span {
      span {
        color: #C61F0C;
      }
    }
  }

  .input-title {
    margin-bottom: 4px;
    font-family: 'RobotoRegular';
    font-size: 14px;
    line-height: calc(1.42857rem * (14 / 16));
    color: ${hexToRGBA('#333333', 0.5)};
    caret-color: ${hexToRGBA('#333333', 0.5)};
  }

  .inputs-block-divider {
    margin-bottom: 46px;
  }

  .top-disclaimer {
    font-family: 'RobotoRegular';
    text-align: center;
    color: #333333;

    span {
      display: block;
      margin-top: 16px;
      margin-bottom: 16px;
    }
  }

  .bottom-disclaimer {
    font-family: 'RobotoRegular';
    font-size: 10px;
    line-height: calc(1.42857rem * (10 / 16));
    text-align: center;
    color: #333333;
    opacity: 0.5;
  }

  button {
    width: 100%;
    margin-bottom: 1rem;
  }

  @media ${({ theme }) => theme.devices.tablet} {
    padding-bottom: 42px;
  }
`;

function OrderStatusResultForm({ order, values = {} }) {
  const [customerEmail, setCustomerEmail] = useState("");
  const [last4SSN, setLast4SSN] = useState("");
  const [token, setToken] = useState(null);
  const [validated, setValidated] = useState(false);
  const [dError, setdError] = useState(null);
  const [product, setProduct] = useState(null);
  const [llcName, setLLCName] = useState("");
  const [llcName2, setLLCName2] = useState("");
  const [llcName3, setLLCName3] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [suffix, setSuffix] = useState("");
  const [ssn, setSSN] = useState("");
  const [thankyou, setThankyou] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [data, setData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [suspendedSubmit, setSuspendedSubmit] = useState(false);

  const prevVerifying =  usePrevious(verifying);

  const handleEmailChange = (e) => {
    const { id, value } = e.target;
    if (id === "verifying") {
      setVerifying(value);
    } else {
      setCustomerEmail(value);
    }
  };

  const handleSubmitCompleted = useCallback((e) => {
    setdError(null);
    e.preventDefault();

    if (verifying) {
      return;
    }

    setSuspendedSubmit(false);

    const form = e.currentTarget;

    const section1 = document.querySelector(".is-invalid");
    if (section1 != null) {
      section1.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      if (form.checkValidity() === false) {
        e.preventDefault();
        e.stopPropagation();
        const section = document.querySelector(".form-control:invalid");
        if (section != null) section.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        axios
          .get(process.env.NEXT_PUBLIC_REACT_APP_API + "/orders/" + order.orderUid + "/PDF", {
            params: {
              email: customerEmail,
              last4: last4SSN,
            },
            responseType: "arraybuffer",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/pdf",
            },
          })
          .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            const file_name = values.orderNumber + ".pdf";
            link.setAttribute("download", file_name);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          })
          .catch((error) => {
            if(error.response) {
              setdError(error.response.statusText);
            }
          });
      }
    }
    setValidated(true);
  }, [order, values, customerEmail, last4SSN, verifying]);

  const handleSubmitTokenRequest = useCallback((e) => {
    setdError(null);
    e.preventDefault();

    if (verifying) {
      return;
    }

    setSuspendedSubmit(false);

    const form = e.currentTarget;

    const section1 = document.querySelector(".is-invalid");
    if (section1 != null) {
      section1.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      if (form.checkValidity() === false) {
        e.preventDefault();
        e.stopPropagation();
        const section = document.querySelector(".form-control:invalid");
        if (section != null) section.scrollIntoView({ behavior: "smooth", block: "start" });
        setValidated(true);
      } else {
        setValidated(true);
        axios
          .post(
            process.env.NEXT_PUBLIC_REACT_APP_API + "/orders/" + values.orderNumber + "/token",
            {},
            {
              params: {
                email: customerEmail,
              },
              headers: { Accept: "application/json" },
            }
          )
          .then((response) => {
            setToken(response.data.token);
            setValidated(false);
            axios
              .get(process.env.NEXT_PUBLIC_REACT_APP_API + "/orders/" + order.orderUid, {
                headers: {
                  Authorization: "Bearer " + response.data.token,
                },
              })
              .then((response) => {
                setData(response.data);
                if (response.data.products && response.data.products.length >= 1) {
                  let lastProduct = response.data.products.length - 1;
                  if (response.data.order.productCode) {
                    if (
                      response.data.order.productCode.code === "EIN_LLC" ||
                      response.data.order.productCode.code === "EIN_SoleProp"
                    ) {
                      setLastName(response.data.products[lastProduct].owner.lastName);
                      setFirstName(response.data.products[lastProduct].owner.firstName);
                      setMiddleName(response.data.products[lastProduct].owner.middleName);
                      setSuffix(response.data.products[lastProduct].owner.suffixName);
                      setSSN(response.data.products[lastProduct].owner.ssn);
                    } else if (
                      response.data.order.productCode.code === "EIN_CCorp" ||
                      response.data.order.productCode.code === "EIN_PSC" ||
                      response.data.order.productCode.code === "EIN_SCorp"
                    ) {
                      setLastName(response.data.products[lastProduct].principalOfficer.lastName);
                      setFirstName(response.data.products[lastProduct].principalOfficer.firstName);
                      setMiddleName(response.data.products[lastProduct].principalOfficer.middleName);
                      setSuffix(response.data.products[lastProduct].principalOfficer.suffixName);
                      setSSN(response.data.products[lastProduct].principalOfficer.ssn);
                    } else if (
                      response.data.order.productCode.code === "EIN_Church" ||
                      response.data.order.productCode.code === "EIN_NonProfit"
                    ) {
                      setLastName(response.data.products[lastProduct].responsibleParty.lastName);
                      setFirstName(response.data.products[lastProduct].responsibleParty.firstName);
                      setMiddleName(response.data.products[lastProduct].responsibleParty.middleName);
                      setSuffix(response.data.products[lastProduct].responsibleParty.suffixName);
                      setSSN(response.data.products[lastProduct].responsibleParty.ssn);
                    } else if (response.data.order.productCode.code === "EIN_Estate") {
                      setLastName(response.data.products[lastProduct].representative.lastName);
                      setFirstName(response.data.products[lastProduct].representative.firstName);
                      setMiddleName(response.data.products[lastProduct].representative.middleName);
                      setSuffix(response.data.products[lastProduct].representative.suffixName);
                      setSSN(response.data.products[lastProduct].representative.ssn);
                    } else if (response.data.order.productCode.code === "EIN_Partnership") {
                      setLastName(response.data.products[lastProduct].primaryPartner.lastName);
                      setFirstName(response.data.products[lastProduct].primaryPartner.firstName);
                      setMiddleName(response.data.products[lastProduct].primaryPartner.middleName);
                      setSuffix(response.data.products[lastProduct].primaryPartner.suffixName);
                      setSSN(response.data.products[lastProduct].primaryPartner.ssn);
                    } else if (response.data.order.productCode.code === "EIN_Trust") {
                      setLastName(response.data.products[lastProduct].trustee.lastName);
                      setFirstName(response.data.products[lastProduct].trustee.firstName);
                      setMiddleName(response.data.products[lastProduct].trustee.middleName);
                      setSuffix(response.data.products[lastProduct].trustee.suffixName);
                      setSSN(response.data.products[lastProduct].trustee.ssn);
                    }
                  } else {
                    let productCodes = response.data.order.productCodes.length - 1;
                    if (
                      response.data.order.productCodes[productCodes].code === "EIN_LLC" ||
                      response.data.order.productCodes[productCodes].code === "EIN_SoleProp"
                    ) {
                      setLastName(response.data.products[lastProduct].owner.lastName);
                      setFirstName(response.data.products[lastProduct].owner.firstName);
                      setMiddleName(response.data.products[lastProduct].owner.middleName);
                      setSuffix(response.data.products[lastProduct].owner.suffixName);
                      setSSN(response.data.products[lastProduct].owner.ssn);
                    } else if (
                      response.data.order.productCodes[productCodes].code === "EIN_CCorp" ||
                      response.data.order.productCodes[productCodes].code === "EIN_PSC" ||
                      response.data.order.productCodes[productCodes].code === "EIN_SCorp"
                    ) {
                      setLastName(response.data.products[lastProduct].principalOfficer.lastName);
                      setFirstName(response.data.products[lastProduct].principalOfficer.firstName);
                      setMiddleName(response.data.products[lastProduct].principalOfficer.middleName);
                      setSuffix(response.data.products[lastProduct].principalOfficer.suffixName);
                      setSSN(response.data.products[lastProduct].principalOfficer.ssn);
                    } else if (
                      response.data.order.productCodes[productCodes].code === "EIN_Church" ||
                      response.data.order.productCodes[productCodes].code === "EIN_NonProfit"
                    ) {
                      setLastName(response.data.products[lastProduct].responsibleParty.lastName);
                      setFirstName(response.data.products[lastProduct].responsibleParty.firstName);
                      setMiddleName(response.data.products[lastProduct].responsibleParty.middleName);
                      setSuffix(response.data.products[lastProduct].responsibleParty.suffixName);
                      setSSN(response.data.products[lastProduct].responsibleParty.ssn);
                    } else if (response.data.order.productCodes[productCodes].code === "EIN_Estate") {
                      setLastName(response.data.products[lastProduct].representative.lastName);
                      setFirstName(response.data.products[lastProduct].representative.firstName);
                      setMiddleName(response.data.products[lastProduct].representative.middleName);
                      setSuffix(response.data.products[lastProduct].representative.suffixName);
                      setSSN(response.data.products[lastProduct].representative.ssn);
                    } else if (response.data.order.productCodes[productCodes].code === "EIN_Partnership") {
                      setLastName(response.data.products[lastProduct].primaryPartner.lastName);
                      setFirstName(response.data.products[lastProduct].primaryPartner.firstName);
                      setMiddleName(response.data.products[lastProduct].primaryPartner.middleName);
                      setSuffix(response.data.products[lastProduct].primaryPartner.suffixName);
                      setSSN(response.data.products[lastProduct].primaryPartner.ssn);
                    } else if (response.data.order.productCodes[productCodes].code === "EIN_Trust") {
                      setLastName(response.data.products[lastProduct].trustee.lastName);
                      setFirstName(response.data.products[lastProduct].trustee.firstName);
                      setMiddleName(response.data.products[lastProduct].trustee.middleName);
                      setSuffix(response.data.products[lastProduct].trustee.suffixName);
                      setSSN(response.data.products[lastProduct].trustee.ssn);
                    }
                  }
                  setProduct(response.data.products[lastProduct]);
                }
              })
              .catch((error) => {
                console.log(error);
                setdError(error.response.statusText);
              });
          })
          .catch((error) => {
            setdError(error.response.statusText);
          });
      }
    }
  }, [order, values, customerEmail, verifying]);

  const handleSubmitLLC = useCallback((e) => {
    setdError(null);
    e.preventDefault();

    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      const section = document.querySelector(".form-control:invalid");
      if (section != null) section.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      const newData = { ...data };

      if (newData.products && newData.products.length >= 1) {
        let lastProduct = newData.products.length - 1;
        newData.products[lastProduct].legalName = llcName;
        if (llcName2 !== "" || llcName3 !== "") {
          newData.products[lastProduct].alternateLegalNames = [];
          if (llcName2 !== "") newData.products[lastProduct].alternateLegalNames.push(llcName2);
          if (llcName3 !== "") newData.products[lastProduct].alternateLegalNames.push(llcName3);
        } else {
          if (newData.products[lastProduct].alternateLegalNames) {
            delete newData.products[lastProduct].alternateLegalNames;
          }
        }
      }

      axios
        .put(process.env.NEXT_PUBLIC_REACT_APP_API + "/orders/" + order.orderUid, newData, {
          crossdomain: true,
          headers: {
            "Content-Type": "application/json;charset=UTF-8",
            "Cache-Control": "no-cache",
            Authorization: "Bearer " + token,
            "Access-Control-Allow-Origin": "*",
          },
        })
        .then((response) => {
          if (response.status === 200) {
            if (selectedFile) {
              const formData = new FormData();
              formData.append("file", selectedFile);
              axios
                .post(
                  process.env.NEXT_PUBLIC_REACT_APP_API +
                    "/orders/" +
                    order.orderUid +
                    "/file?fileType=articles_of_incorporation",
                  formData,
                  {
                    crossdomain: true,
                    headers: {
                      "Content-Type": "multipart/form-data",
                      "Cache-Control": "no-cache",
                      Authorization: "Bearer " + token,
                      "Access-Control-Allow-Origin": "*",
                    },
                  }
                )
                .then((response) => {
                  if (response.status === 200) {
                    setThankyou(true);
                  }
                })
                .catch((error) => {
                  setdError(error.message);
                });
            } else setThankyou(true);
          }
        })
        .catch((error) => {
          console.log(error);
          setdError(error.message);
        });
    }
    setValidated(true);
  }, [order, data, llcName, llcName2, llcName3, token, selectedFile]);

  const handleSubmitMismatch = useCallback((e) => {
    setdError(null);
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      const section = document.querySelector(".form-control:invalid");
      if (section != null) section.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      const newData = { ...data };

      if (newData.products && newData.products.length >= 1) {
        let lastProduct = newData.products.length - 1;
        if (newData.order.productCode) {
          if (newData.order.productCode.code === "EIN_LLC" || newData.order.productCode.code === "EIN_SoleProp") {
            newData.products[lastProduct].owner.lastName = lastName;
            newData.products[lastProduct].owner.firstName = firstName;
            newData.products[lastProduct].owner.middleName = middleName;
            newData.products[lastProduct].owner.suffixName = suffix;
            newData.products[lastProduct].owner.ssn = ssn;
          } else if (
            newData.order.productCode.code === "EIN_CCorp" ||
            newData.order.productCode.code === "EIN_PSC" ||
            newData.order.productCode.code === "EIN_SCorp"
          ) {
            newData.products[lastProduct].principalOfficer.lastName = lastName;
            newData.products[lastProduct].principalOfficer.firstName = firstName;
            newData.products[lastProduct].principalOfficer.middleName = middleName;
            newData.products[lastProduct].principalOfficer.suffixName = suffix;
            newData.products[lastProduct].principalOfficer.ssn = ssn;
          } else if (newData.order.productCode.code === "EIN_Church" || newData.order.productCode.code === "EIN_NonProfit") {
            newData.products[lastProduct].responsibleParty.lastName = lastName;
            newData.products[lastProduct].responsibleParty.firstName = firstName;
            newData.products[lastProduct].responsibleParty.middleName = middleName;
            newData.products[lastProduct].responsibleParty.suffixName = suffix;
            newData.products[lastProduct].responsibleParty.ssn = ssn;
          } else if (newData.order.productCode.code === "EIN_Estate") {
            newData.products[lastProduct].representative.lastName = lastName;
            newData.products[lastProduct].representative.firstName = firstName;
            newData.products[lastProduct].representative.middleName = middleName;
            newData.products[lastProduct].representative.suffixName = suffix;
            newData.products[lastProduct].representative.ssn = ssn;
          } else if (newData.order.productCode.code === "EIN_Partnership") {
            newData.products[lastProduct].primaryPartner.lastName = lastName;
            newData.products[lastProduct].primaryPartner.firstName = firstName;
            newData.products[lastProduct].primaryPartner.middleName = middleName;
            newData.products[lastProduct].primaryPartner.suffixName = suffix;
            newData.products[lastProduct].primaryPartner.ssn = ssn;
          } else if (newData.order.productCode.code === "EIN_Trust") {
            newData.products[lastProduct].trustee.lastName = lastName;
            newData.products[lastProduct].trustee.firstName = firstName;
            newData.products[lastProduct].trustee.middleName = middleName;
            newData.products[lastProduct].trustee.suffixName = suffix;
            newData.products[lastProduct].trustee.ssn = ssn;
          }
        } else {
          let productCodes = newData.order.productCodes.length - 1;
          if (
            newData.order.productCodes[productCodes].code === "EIN_LLC" ||
            newData.order.productCodes[productCodes].code === "EIN_SoleProp"
          ) {
            newData.products[lastProduct].owner.lastName = lastName;
            newData.products[lastProduct].owner.firstName = firstName;
            newData.products[lastProduct].owner.middleName = middleName;
            newData.products[lastProduct].owner.suffixName = suffix;
            newData.products[lastProduct].owner.ssn = ssn;
          } else if (
            newData.order.productCodes[productCodes].code === "EIN_CCorp" ||
            newData.order.productCodes[productCodes].code === "EIN_PSC" ||
            newData.order.productCodes[productCodes].code === "EIN_SCorp"
          ) {
            newData.products[lastProduct].principalOfficer.lastName = lastName;
            newData.products[lastProduct].principalOfficer.firstName = firstName;
            newData.products[lastProduct].principalOfficer.middleName = middleName;
            newData.products[lastProduct].principalOfficer.suffixName = suffix;
            newData.products[lastProduct].principalOfficer.ssn = ssn;
          } else if (
            newData.order.productCodes[productCodes].code === "EIN_Church" ||
            newData.order.productCodes[productCodes].code === "EIN_NonProfit"
          ) {
            newData.products[lastProduct].responsibleParty.lastName = lastName;
            newData.products[lastProduct].responsibleParty.firstName = firstName;
            newData.products[lastProduct].responsibleParty.middleName = middleName;
            newData.products[lastProduct].responsibleParty.suffixName = suffix;
            newData.products[lastProduct].responsibleParty.ssn = ssn;
          } else if (newData.order.productCodes[productCodes].code === "EIN_Estate") {
            newData.products[lastProduct].representative.lastName = lastName;
            newData.products[lastProduct].representative.firstName = firstName;
            newData.products[lastProduct].representative.middleName = middleName;
            newData.products[lastProduct].representative.suffixName = suffix;
            newData.products[lastProduct].representative.ssn = ssn;
          } else if (newData.order.productCodes[productCodes].code === "EIN_Partnership") {
            newData.products[lastProduct].primaryPartner.lastName = lastName;
            newData.products[lastProduct].primaryPartner.firstName = firstName;
            newData.products[lastProduct].primaryPartner.middleName = middleName;
            newData.products[lastProduct].primaryPartner.suffixName = suffix;
            newData.products[lastProduct].primaryPartner.ssn = ssn;
          } else if (newData.order.productCodes[productCodes].code === "EIN_Trust") {
            newData.products[lastProduct].trustee.lastName = lastName;
            newData.products[lastProduct].trustee.firstName = firstName;
            newData.products[lastProduct].trustee.middleName = middleName;
            newData.products[lastProduct].trustee.suffixName = suffix;
            newData.products[lastProduct].trustee.ssn = ssn;
          }
        }
      }

      axios
        .put(process.env.NEXT_PUBLIC_REACT_APP_API + "/orders/" + order.orderUid, newData, {
          crossdomain: true,
          headers: {
            "Content-Type": "application/json;charset=UTF-8",
            "Cache-Control": "no-cache",
            Authorization: "Bearer " + token,
            "Access-Control-Allow-Origin": "*",
          },
        })
        .then((response) => {
          if (response.status === 200) {
            setThankyou(true);
          }
        })
        .catch((error) => {
          setdError(error.message);
        });
    }
    setValidated(true);
  }, [order, data, token, firstName, middleName, lastName, suffix, ssn]);

  const handleSubmit = useCallback((e) => {
    if (order.processingStatus === "Completed") {
      return handleSubmitCompleted(e);
    }

    if (
      order.processingStatus === "Error" && !product && 
      order.processingError === "Error101NameConflict"
    ) {
      return handleSubmitTokenRequest(e);
    }

    if (
      order.processingStatus === "Error" && !product &&
      order.processingError === "MismatchError" &&
      order.productCodes.length === 1
    ) {
      return handleSubmitTokenRequest(e);
    }

    if (
      order.processingStatus === "Error" && product &&
      order.processingError === "MismatchError" &&
      order.productCodes.length === 1
    ) {
      return handleSubmitMismatch(e);
    }

    if (
      order.processingStatus === "Error" && product && product.code.code === "EIN_LLC" &&
      order.processingError === "Error101NameConflict" 
    ) {
      return handleSubmitLLC(e);
    }
  }, [
    order,
    handleSubmitCompleted,
    handleSubmitTokenRequest,
    handleSubmitMismatch,
    handleSubmitLLC,
  ]);

  useEffect(() => {
    if (product?.legalName) {
      setLLCName(product?.legalName || "");
    }
    if (product?.alternateLegalNames && product?.alternateLegalNames[0]) {
      setLLCName2(product?.alternateLegalNames[0] || "");
    }
    if (product?.alternateLegalNames && product?.alternateLegalNames[1]) {
      setLLCName3(product?.alternateLegalNames[1] || "");
    }
  }, [product]);

  useEffect(() => {
    if (prevVerifying && !verifying && suspendedSubmit) {
      document.getElementById("OrderStatusSubmitButton1")?.click();
      document.getElementById("OrderStatusSubmitButton2")?.click();
      document.getElementById("OrderStatusSubmitButton3")?.click();
    }
  }, [verifying, prevVerifying, suspendedSubmit]);

  if (thankyou) {
    return (
      <StyledOrderStatusResultForm>
        <Row>
          <Col xs={12}>
            <div className="inputs-block-divider" />
          </Col>
          <Col xs={12}>
            <div className="inputs-block-title">
              <span>
                Thank You
              </span>
            </div>
          </Col>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                Your Order is updated
              </span>
            </div>
          </Col>
          <Col xs={12}>
            <div className="inputs-block-divider" />
          </Col>
        </Row>
      </StyledOrderStatusResultForm>
    );
  }

  let oneDay = new Date();
  let now = new Date();
  oneDay.setDate(now.getDate() + 1);

  return (
    <StyledOrderStatusResultForm
      id="kform"
      noValidate
      validated={validated}
      onSubmit={handleSubmit}
    >
      <Row>
        <Col xs={12}>
          <div className="inputs-block-divider" />
        </Col>
        <Col xs={12}>
          <div className="inputs-block-title">
            <span>
              {order.productCodes.length === 1
                ? order.productCodes[0].customerName
                : order.productCodes[1].customerName}
            </span>
          </div>
        </Col>
      </Row>
      {order.processingStatus === "Completed" && (
        <>
          {order.productCodes.length === 2 && (
            <Row>
              <Col xs={12}>
                <div className="top-disclaimer">
                  <span>Your LLC Inc. was generated and sent to the e-mail address on file.</span>
                </div>
              </Col>
            </Row>
          )}
          {order.productCodes.length !== 2 && (
            <Row>
              <Col xs={12}>
                <div className="top-disclaimer">
                  <span>
                    Your EIN/Tax ID was generated and sent to the e-mail address on file. To download a copy here,
                    please enter your e-mail address and last 4 digits of the Responsible Party&apos;s Social Security Number
                    below.
                  </span>
                </div>
              </Col>
              <Col xs={12}>
                <TextInput readOnly label="Order Number" value={values.orderNumber} />
              </Col>
              <Col xs={12} md={8}>
                <TextInput
                  id="customerEmail"
                  type="email"
                  required
                  label="Email Address"
                  placeholder="Enter E-mail"
                  defaultInvalidMessage="You must provide a valid email address"
                  pattern="^([a-zA-Z0-9_\-\+\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$"
                  value={customerEmail}
                  loading={verifying}
                  onChange={handleEmailChange}
                  onMouseDown={() => setSuspendedSubmit(true)}
                />
              </Col>
              <Col xs={12} md={4}>
                <MaskInput
                  id="last4SSN"
                  required
                  label="Last 4 of SSN"
                  placeholder="XXXX"
                  pattern=".{4,}"
                  mask="9999"
                  defaultInvalidMessage="Please specify last 4 digits of the Responsible Party's Social Security Number"
                  value={last4SSN}
                  onChange={(e) => setLast4SSN(e.target.value)}
                />
              </Col>
              <Col xs={12}>
                <div className="invalid-feedback d-block mt-0 mb-2">{dError}</div>
              </Col>
              <Col xs={12}>
                <div className="inputs-block-divider" />
              </Col>
              <Col xs={0} md={3} />
              <Col xs={12} md={6}>
                <Button type="submit" id="OrderStatusSubmitButton1">
                  Download
                </Button>
              </Col>
              <Col xs={0} md={3} />
            </Row>
          )}
        </>
      )}
      {order.processingStatus === "Completed_SS4" && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                Your order has been completed. We have received your signed SS-4 form and have submitted it directly to
                the IRS. You can expect to receive your EIN from the IRS by mail shortly.
              </span>
            </div>
          </Col>
        </Row>
      )}
      {order.processingStatus === "RAaddressAdded" && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                Your Resident Agent has been assigned. Your Address to use for official communications with the State is:
              </span>
            </div>
          </Col>
          {order.productResult && (
            <Col xs={12}>
              <Row>
                <Col xs={0} md={2} />
                <Col xs={6} md={4}>
                  <div className="input-title">
                    <strong>
                      Address:
                    </strong>
                  </div>
                </Col>
                <Col xs={6} md={4}>
                  <div className="input-title">
                    <span>
                      {order.productResult?.address1}
                    </span>
                  </div>
                </Col>
                <Col xs={0} md={2} />
              </Row>
              <Row>
                <Col xs={0} md={2} />
                <Col xs={6} md={4}>
                  <div className="input-title">
                    <strong>
                      City:
                    </strong>
                  </div>
                </Col>
                <Col xs={6} md={4}>
                  <div className="input-title">
                    <span>
                      {order.productResult?.city}
                    </span>
                  </div>
                </Col>
                <Col xs={0} md={2} />
              </Row>
              <Row>
                <Col xs={0} md={2} />
                <Col xs={6} md={4}>
                  <div className="input-title">
                    <strong>
                      State / Province:
                    </strong>
                  </div>
                </Col>
                <Col xs={6} md={4}>
                  <div className="input-title">
                    <span>
                      {STATES[order.productResult?.state]}
                    </span>
                  </div>
                </Col>
                <Col xs={0} md={2} />
              </Row>
              <Row>
                <Col xs={0} md={2} />
                <Col xs={6} md={4}>
                  <div className="input-title">
                    <strong>
                      ZIP / Postal Code:
                    </strong>
                  </div>
                </Col>
                <Col xs={6} md={4}>
                  <div className="input-title">
                    <span>
                      {order.productResult?.zip}
                    </span>
                  </div>
                </Col>
                <Col xs={0} md={2} />
              </Row>
            </Col>
          )}
        </Row>
      )}
      {order.processingStatus === "Cancelled" && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                Your order was cancelled at your request. A full refund was processed to the card on file. Please allow
                3-5 business days for that refund to appear on your account.
              </span>
            </div>
          </Col>
        </Row>
      )}
      {order.processingStatus === "Rejected" && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                Your order was rejected due to the Secretary of State needing an alternative name, you may still refile with the updated name.
              </span>
            </div>
          </Col>
        </Row>
      )}
      {order.processingStatus === "Abandoned" && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                Your filing has been abandoned. For questions please contact customer service.
              </span>
            </div>
          </Col>
        </Row>
      )}
      {order.processingStatus === "Processing" && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                Your submission is being reviewed by our Specialists and should be completed shortly. You will receive
                an e-mail with your official EIN/Tax ID documentation when complete.
                <br />
                Estimated Completion Time:&nbsp;
                {order.processingOption === "STANDARD" && (
                  <>
                    (
                    {oneDay.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                    &nbsp; 12:00 PM EST)
                  </>
                )}
                {order.processingOption === "RUSH" && (
                  <>
                    (
                    {now.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                    &nbsp; 8:00 PM EST)
                  </>
                )}
              </span>
            </div>
          </Col>
        </Row>
      )}
      {order.processingStatus === "Error" &&
        !product &&
        order.processingError !== "Error101NameConflict" &&
        (order.processingError !== "MismatchError" ||
          (order.processingError === "MismatchError" && order.productCodes.length === 2)) &&
        order.processingError !== "SS4" && (
          <Row>
            <Col xs={12}>
              <div className="top-disclaimer">
                <span>
                  Our specialists have identified an issue with your submission that prevents us from completing the
                  order without additional verification. Please reference the verification section below, or check your
                  e-mail for a follow-up request from us. Please note this is Urgent and we will not be able to proceed
                  until we hear from you.
                  <br />
                  <br />
                  Additional details are required for your submission. You will be contacted by e-mail to provide these
                  details. Please keep a close watch for this e-mail.
                </span>
              </div>
            </Col>
          </Row>
        )}
      {((order.processingStatus === "Error" && !product && order.processingError === "SS4") ||
        order.processingStatus === "AttemptedNeedsInfo") && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                Our specialists have identified an issue with your submission that prevents us from completing the order
                without additional verification. Please reference the verification section below, or check your e-mail
                for a follow-up request from us. Please note this is Urgent and we will not be able to proceed until we
                hear from you.
                <br />
                <br />
                Your submission requires additional documentation which we will submit to the IRS for you. You will
                receive a Docusign in your e-mail to authorize this submission. Please keep a close watch for this
                e-mail.
              </span>
            </div>
          </Col>
        </Row>
      )}
      {order.processingStatus === "Error" && !product && order.processingError === "Error101NameConflict" && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                Our specialists have identified an issue with your submission that prevents us from completing the order
                without additional verification. Please reference the verification section below, or check your e-mail
                for a follow-up request from us. Please note this is Urgent and we will not be able to proceed until we
                hear from you.
              </span>
            </div>
          </Col>
          <Col xs={12}>
            <TextInput readOnly label="Order Number" value={values.orderNumber} />
          </Col>
          <Col xs={12}>
            <TextInput
              id="customerEmail"
              type="email"
              required
              label="Email Address"
              placeholder="Enter E-mail"
              defaultInvalidMessage="You must provide a valid email address"
              pattern="^([a-zA-Z0-9_\-\+\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$"
              value={customerEmail}
              loading={verifying}
              onChange={handleEmailChange}
              onMouseDown={() => setSuspendedSubmit(true)}
            />
          </Col>
          <Col xs={12}>
            <div className="invalid-feedback d-block mt-0 mb-2">{dError}</div>
          </Col>
          <Col xs={12}>
            <div className="inputs-block-divider" />
          </Col>
          <Col xs={0} md={3} />
          <Col xs={12} md={6}>
            <Button type="submit" id="OrderStatusSubmitButton2">
              Submit
            </Button>
          </Col>
          <Col xs={0} md={3} />
        </Row>
      )}
      {order.processingStatus === "Error" &&
        !product &&
        order.processingError === "MismatchError" &&
        order.productCodes.length === 1 && (
          <Row>
            <Col xs={12}>
              <div className="top-disclaimer">
                <span>
                  Our specialists have identified an issue with your submission that prevents us from completing the
                  order without additional verification. Please reference the verification section below, or check your
                  e-mail for a follow-up request from us. Please note this is Urgent and we will not be able to proceed
                  until we hear from you.
                </span>
              </div>
            </Col>
            <Col xs={12}>
              <TextInput readOnly label="Order Number" value={values.orderNumber} />
            </Col>
            <Col xs={12}>
              <TextInput
                id="customerEmail"
                type="email"
                required
                label="Email Address"
                placeholder="Enter E-mail"
                defaultInvalidMessage="You must provide a valid email address"
                pattern="^([a-zA-Z0-9_\-\+\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$"
                value={customerEmail}
                loading={verifying}
                onChange={handleEmailChange}
                onMouseDown={() => setSuspendedSubmit(true)}
              />
            </Col>
            <Col xs={12}>
              <div className="invalid-feedback d-block mt-0 mb-2">{dError}</div>
            </Col>
            <Col xs={12}>
              <div className="inputs-block-divider" />
            </Col>
            <Col xs={0} md={3} />
            <Col xs={12} md={6}>
              <Button type="submit" id="OrderStatusSubmitButton3">
                Submit
              </Button>
            </Col>
            <Col xs={0} md={3} />
          </Row>
        )}
      {(order.processingStatus === "Error" &&
        product &&
        order.processingError === "MismatchError" &&
        order.productCodes.length === 1) && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                We have encountered an issue verifying the Responsible Party’s identifying information. In your
                submission you provided:
              </span>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              id="firstName"
              required
              label="First Name"
              placeholder="First Name"
              pattern="^[ a-zA-Z&\\-]+$"
              defaultInvalidMessage="Please provide a valid name"
              onKeyPress={(event) => {
                if (!/[ a-zA-Z&\\-]/.test(event.key)) event.preventDefault();
              }}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              id="middleName"
              label="Middle Name"
              placeholder="Middle Name (optional)"
              pattern="^[ a-zA-Z&\\-]+$"
              onKeyPress={(event) => {
                if (!/[ a-zA-Z&\\-]/.test(event.key)) event.preventDefault();
              }}
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              id="lastName"
              required
              label="Last Name"
              placeholder="Last Name"
              pattern="^[ a-zA-Z&\\-]+$"
              defaultInvalidMessage="Please provide last name"
              onKeyPress={(event) => {
                if (!/[ a-zA-Z&\\-]/.test(event.key)) event.preventDefault();
              }}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Col>
          <Col xs={12} md={6}>
            <Select
              id="responsiblePartySuffixName"
              label="Suffix"
              placeholder="Please select"
              validated={validated}
              options={SUFFIX_OPTIONS}
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
            />
          </Col>
          <Col xs={12}>
            <MaskInput
              id="SSN"
              hint={(
                <>
                  <strong>Why do we need this?</strong>
                  <br />
                  Your valid Social Security Number is needed by the IRS to obtain an EIN. 
                  Rest assured, all Social Security Numbers are stored in a fully protected fashion.
                </>
              )}
              required
              label="Social Security Number"
              placeholder="123-45-6789"
              pattern=".{11,}"
              mask="999-99-9999"
              defaultInvalidMessage="Please provide a valid Social Security Number Ex: 123-45-6789"
              value={ssn}
              onChange={(e) => setSSN(e.target.value)}
            />
          </Col>
          <Col xs={12}>
            <div className="invalid-feedback d-block mt-0 mb-2">{dError}</div>
          </Col>
          <Col xs={12}>
            <div className="bottom-disclaimer">
              <span>
                Our validation has shown that the provided Name does not match the Social Security Number provided.
                Please correct the errors in these fields and hit Submit Updated Info. 
                Please note, this must correspond to the Name and Social Security Number on file with the Social Security Administration and IRS. 
                If you recently changed your name due to marriage, separation, or other life event, please only submit the official name on file.
              </span>
            </div>
          </Col>
          <Col xs={12}>
            <div className="inputs-block-divider" />
          </Col>
          <Col xs={0} md={3} />
            <Col xs={12} md={6}>
              <Button type="submit">
                Update Info
              </Button>
            </Col>
            <Col xs={0} md={3} />
        </Row>
      )}
      {order.processingStatus === "Error" && product &&
      order.processingError === "Error101NameConflict" &&
      product.code.code === "EIN_LLC" && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                We have encountered an issue with the LLC name provided in your submission. 
                This could mean that another LLC in another state has a similar name. 
                Please verify that the name provided below is accurate, 
                and was the name submitted to the state in which you registered the LLC.
                <br />
                If you need to make a correction, edit the name below. 
                If the name is correct, we ask that you please provide two alternate names, 
                which could be very slight variations of the provided LLC name.
              </span>
            </div>
          </Col>
          <Col xs={12}>
            <TextInput
              id="llcName"
              required
              label="LLC Name"
              placeholder="LLC Name"
              pattern="(?!.*(?:[ ][cC][oO][rR][pP]$))(?!.*(?:[ ][iI][nN][cC]$))^[ a-zA-Z0-9\\&-]+$"
              maxLength="70"
              defaultInvalidMessage="Please provide a valid organization name"
              value={llcName}
              onChange={(e) => setLLCName(e.target.value)}
            />
          </Col>
          <Col xs={12}>
            <TextInput
              id="llcName2"
              label="Alternate LLC Name"
              placeholder="Alternate LLC Name"
              pattern="(?!.*(?:[ ][cC][oO][rR][pP]$))(?!.*(?:[ ][iI][nN][cC]$))^[ a-zA-Z0-9\\&-]+$"
              maxLength="70"
              defaultInvalidMessage="Please provide a valid organization name"
              value={llcName2}
              onChange={(e) => setLLCName2(e.target.value)}
            />
          </Col>
          <Col xs={12}>
            <TextInput
              id="llcName3"
              label="Alternate LLC Name 2"
              placeholder="Alternate LLC Name 2"
              pattern="(?!.*(?:[ ][cC][oO][rR][pP]$))(?!.*(?:[ ][iI][nN][cC]$))^[ a-zA-Z0-9\\&-]+$"
              maxLength="70"
              defaultInvalidMessage="Please provide a valid organization name"
              value={llcName3}
              onChange={(e) => setLLCName3(e.target.value)}
            />
          </Col>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                To aid with this, you may also attach your Articles of Incorporation below:
              </span>
            </div>
          </Col>
          <Col xs={12}>
            <div className="inputs-block-divider" />
          </Col>
          <Col xs={12}>
            <Form.File id="formcheck-api-regular">
              <Form.File.Input className="articles-input" onChange={(e) => setSelectedFile(e.target.files[0])} />
            </Form.File>
          </Col>
          <Col xs={12}>
            <div className="invalid-feedback d-block mt-0 mb-2">{dError}</div>
          </Col>
          <Col xs={12}>
            <div className="inputs-block-divider" />
          </Col>
          <Col xs={0} md={3} />
            <Col xs={12} md={6}>
              <Button type="submit">
                Update Info
              </Button>
            </Col>
            <Col xs={0} md={3} />
        </Row>
      )}
      {order.processingStatus === "Error" && product &&
      order.processingError === "Error101NameConflict" &&
      product.code.code !== "EIN_LLC" && (
        <Row>
          <Col xs={12}>
            <div className="top-disclaimer">
              <span>
                Our specialists have identified an issue with your submission that prevents us from completing the order without additional verification. 
                Please reference the verification section below, or check your e-mail for a follow-up request from us. 
                Please note this is Urgent and we will not be able to proceed until we hear from you.
              </span>
            </div>
          </Col>
        </Row>
      )}
    </StyledOrderStatusResultForm>
  );
}

export default OrderStatusResultForm;
